import { appApiSlice } from 'src/api/app-ApiSlice';
import { TAG_PARTICIPANT_LISTS, TAG_PROJECT_USERS } from 'src/api/api-tags';
import {
  AddParticipantToListRequest,
  ContactResponse,
  CreateParticipantListRequest,
  DeleteContactRequest,
  DeleteParticipantFromListRequest,
  DeleteParticipantListRequest,
  InviteParticipantListRequest,
  InviteUserRequest,
  ModifyContactRequest,
  ParticipantList,
  ParticipantListResponse,
  UpdateParticipantInListRequest,
  UpdateParticipantListRequest,
} from 'src/model/api/contact.model';

// the v1 participant list endpoints answer a failed save with HTTP 200 and id: null
export const rejectFailedParticipantListSave = (response: ParticipantListResponse): Promise<ParticipantListResponse> =>
  response.id === null
    ? Promise.reject({ data: { message: response.message } })
    : Promise.resolve(response);

export const contactApiSlice = appApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    inviteUser: builder.mutation<ContactResponse, InviteUserRequest>({
      query: (body) => ({
        url: '/v1/contact/invite-user',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_PROJECT_USERS],
    }),
    modifyContact: builder.mutation<ContactResponse | null, ModifyContactRequest>({
      query: (body) => ({
        url: '/v1/contact/modify',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_PROJECT_USERS],
    }),
    deleteContact: builder.mutation<ContactResponse | null, DeleteContactRequest>({
      query: (body) => ({
        url: '/v1/contact',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: [TAG_PROJECT_USERS],
    }),
    searchParticipantLists: builder.query<ParticipantList[], string>({
      query: (searchString) => ({
        url: '/v1/contact/participant-list',
        params: { list_search_string: searchString },
      }),
      transformResponse: (response: ParticipantList[] | null) => response ?? [],
      providesTags: [TAG_PARTICIPANT_LISTS],
    }),
    inviteParticipantList: builder.mutation<ContactResponse | null, InviteParticipantListRequest>({
      query: (body) => ({
        url: '/v1/contact/participants-invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_PROJECT_USERS],
    }),
    createParticipantList: builder.mutation<ParticipantListResponse, CreateParticipantListRequest>({
      query: (body) => ({
        url: '/v1/contact/participant-list',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_PARTICIPANT_LISTS],
    }),
    updateParticipantList: builder.mutation<ParticipantListResponse, UpdateParticipantListRequest>({
      query: (body) => ({
        url: '/v1/contact/participant-list',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [TAG_PARTICIPANT_LISTS],
    }),
    deleteParticipantList: builder.mutation<ParticipantListResponse, DeleteParticipantListRequest>({
      query: (body) => ({
        url: '/v1/contact/participant-list',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: [TAG_PARTICIPANT_LISTS],
    }),
    addParticipantToList: builder.mutation<ParticipantListResponse, AddParticipantToListRequest>({
      query: (body) => ({
        url: '/v1/contact/participant-list-participant',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_PARTICIPANT_LISTS],
    }),
    updateParticipantInList: builder.mutation<ParticipantListResponse, UpdateParticipantInListRequest>({
      query: (body) => ({
        url: '/v1/contact/participant-list-participant',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [TAG_PARTICIPANT_LISTS],
    }),
    deleteParticipantFromList: builder.mutation<ParticipantListResponse, DeleteParticipantFromListRequest>({
      query: (body) => ({
        url: '/v1/contact/participant-list-participant',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: [TAG_PARTICIPANT_LISTS],
    }),
  }),
  overrideExisting: false,
});

export const {
  useInviteUserMutation,
  useModifyContactMutation,
  useDeleteContactMutation,
  useSearchParticipantListsQuery,
  useInviteParticipantListMutation,
  useCreateParticipantListMutation,
  useUpdateParticipantListMutation,
  useDeleteParticipantListMutation,
  useAddParticipantToListMutation,
  useUpdateParticipantInListMutation,
  useDeleteParticipantFromListMutation,
} = contactApiSlice;
